// roughness calibration from several hydrant tests: one k per group (material + age class, or per branch),
// fitted so that the calculated pressure drops (static − flowing) match all measured drops (least squares)
import { simulate, kOf, matOf } from './aquaNet';
import { pathPipes } from './aquaCalib';

const ageCls = (y) => (!y ? 'u' : y < 1970 ? '0' : y < 1990 ? '1' : y < 2010 ? '2' : '3');
const LO = Math.log(0.005), HI = Math.log(20), PHI = (Math.sqrt(5) - 1) / 2;

// flow path of a test = every pipe whose flow changes by ≥ 10 % of the withdrawal when the hydrant opens (incl. parallel loop branches)
async function flowPaths(project, cfg, meas) {
  const o = { noHydRes: true, noHyd: true }, c = { ...cfg, fire: null };
  const st = await simulate(project, c, o), q0 = {};
  (st.links || []).forEach((l) => { if (l.pipeId) q0[l.id] = l.q; });
  const out = [];
  for (const m of meas) {
    const r = await simulate(project, c, { ...o, hydQ: { [m.hid]: m.q } }), ids = new Set();
    (r.links || []).forEach((l) => { if (l.pipeId && Math.abs(l.q - (q0[l.id] ?? 0)) >= Math.max(0.1 * m.q, 0.5)) ids.add(l.pipeId); });
    out.push(ids.size ? [...ids] : pathPipes(project, m.hid));
  }
  return out;
}

export function calGroups(project, meas, mode, P) {
  const paths = P || meas.map((m) => pathPipes(project, m.hid)), byId = Object.fromEntries(project.pipes.map((p) => [p.id, p])), G = {};
  if (mode === 'strand') {
    const cnt = {}; paths.forEach((ps) => ps.forEach((id) => { cnt[id] = (cnt[id] || 0) + 1; }));
    meas.forEach((m, i) => { const own = paths[i].filter((id) => cnt[id] === 1); if (own.length) G[`s:${m.hid}`] = { key: `s:${m.hid}`, kind: 'strand', hid: m.hid, pipes: own }; });
    const sh = Object.keys(cnt).filter((id) => cnt[id] > 1);
    if (sh.length) G.shared = { key: 'shared', kind: 'shared', pipes: sh, fixed: true };
  } else {
    [...new Set(paths.flat())].forEach((id) => {
      const p = byId[id], key = `${matOf(p) || p.material || '?'}|${ageCls(+p.hyd?.year)}`;
      (G[key] ||= { key, kind: 'mat', mat: p.material || '?', age: ageCls(+p.hyd?.year), pipes: [] }).pipes.push(id);
    });
  }
  const pf = Object.fromEntries((project.fittings || []).map((f) => [f.id, f]));
  return Object.values(G).map((g) => ({ ...g, kOld: Math.exp(g.pipes.reduce((s, id) => s + Math.log(kOf(byId[id], pf)), 0) / g.pipes.length) }));
}

export async function calibMulti(project, cfg, meas, mode, onStep) {
  const st0 = await simulate(project, { ...cfg, fire: null }, { noHydRes: true, noHyd: true }), dry = meas.filter((m) => !Number.isFinite(st0.nodes?.[m.hid]?.bar)).map((m) => m.hid);
  if (dry.length) return { error: 'dry', ids: dry };
  const groups = calGroups(project, meas, mode, await flowPaths(project, cfg, meas)), free = groups.filter((g) => !g.fixed);
  if (!free.length) return { error: 'path' };
  let n = 0;
  const run = async (km) => {
    const set = {}; free.forEach((g) => g.pipes.forEach((id) => { set[id] = km[g.key]; }));
    const p = { ...project, aqua: { ...(project.aqua || {}), hl: 'DW' }, pipes: project.pipes.map((x) => (set[x.id] != null ? { ...x, hyd: { ...(x.hyd || {}), k: set[x.id] } } : x)) };
    const o = { noHydRes: true, noHyd: true }, c = { ...cfg, fire: null };
    const st = await simulate(p, c, o); n += 1; onStep?.(n);
    const dp = [];
    for (const m of meas) { const r = await simulate(p, c, { ...o, hydQ: { [m.hid]: m.q } }); n += 1; onStep?.(n); dp.push((st.nodes?.[m.hid]?.bar ?? NaN) - (r.nodes?.[m.hid]?.bar ?? NaN)); }
    return dp;
  };
  const sse = (dp) => dp.reduce((s, d, i) => s + (Number.isFinite(d) ? (d - meas[i].dp) ** 2 : 1e3), 0);
  const km = Object.fromEntries(free.map((g) => [g.key, g.kOld])), before = await run(km);
  // coordinate descent (golden-section on log k per group) until the fit stops improving; correlated groups need several sweeps
  let last = sse(before);
  for (let sweep = 0; sweep < 8; sweep++) {
    for (const g of free) {
      let a = LO, b = HI, c = b - PHI * (b - a), d = a + PHI * (b - a);
      const f = async (x) => sse(await run({ ...km, [g.key]: Math.exp(x) }));
      let fc = await f(c), fd = await f(d);
      for (let i = 0; i < 11; i++) {
        if (fc < fd) { b = d; d = c; fd = fc; c = b - PHI * (b - a); fc = await f(c); } else { a = c; c = d; fc = fd; d = a + PHI * (b - a); fd = await f(d); }
      }
      km[g.key] = +Math.exp((a + b) / 2).toFixed(3);
    }
    const now = sse(await run(km));
    if (last - now < 1e-5) break;
    last = now;
  }
  const fitted = await run(km);
  // a group that hardly changes any drop when k is doubled cannot be determined from these measurements
  for (const g of free) {
    const dp2 = await run({ ...km, [g.key]: km[g.key] * 2 });
    g.na = dp2.every((x, i) => Math.abs(x - fitted[i]) < 0.03);
    g.kNew = g.na ? g.kOld : km[g.key];
  }
  // preview = exactly the state that "apply" produces (undeterminable groups keep their old k)
  const after = free.some((g) => g.na) ? await run(Object.fromEntries(free.map((g) => [g.key, g.kNew]))) : fitted;
  return { groups, fit: meas.map((m, i) => ({ ...m, before: before[i], after: after[i] })), sims: n };
}
